import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, GraduationCap, Users, Target, Shield, Globe, TrendingUp, CheckCircle, ArrowRight, ChevronDown } from 'lucide-react';
import { FadeIn, SlideIn, ScaleIn, Stagger, StaggerItem } from '../components/animations';
import { Card, CardBody } from '../components/ui';
import { faqAPI } from '../services/api';

interface FAQ {
  _id: string;
  question: string;
  answer: string;
}

const About: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [faqs, setFaqs] = useState<FAQ[]>([]);

  useEffect(() => {
    faqAPI.getAll().then(res => setFaqs(res.data.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About BSAF</h1>
            <p className="text-2xl text-blue-100 max-w-3xl mx-auto">Building Africa's Smart Generation</p>
          </FadeIn>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
                <Target className="w-4 h-4" />
                <span className="text-sm font-semibold">Our Mission</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Empowering Africa's Future</h2>
            </div>
          </FadeIn>
          
          <SlideIn direction="up" delay={0.2}>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                BSAF (Best Scholars Africa) is dedicated to addressing education, employment, and digital skills challenges across Africa. We provide a trusted platform that connects talented individuals with verified opportunities while protecting them from fraud and exploitation.
              </p>
              <p>
                Our mission is to reduce youth unemployment, bridge the skills gap, and create equal access to opportunities regardless of location or background.
              </p>
            </div>
          </SlideIn>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">What We Do</h2>
          </FadeIn>
          
          <Stagger>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Briefcase className="w-8 h-8" />,
                  title: 'Job Opportunities',
                  desc: 'Connect with verified employers and access thousands of job opportunities across Africa. All postings are screened to protect you from scams.',
                  color: 'from-blue-500 to-indigo-600'
                },
                {
                  icon: <GraduationCap className="w-8 h-8" />,
                  title: 'Scholarships & Training',
                  desc: 'Discover scholarships, internships, and training programs to advance your education and career development.',
                  color: 'from-green-500 to-teal-600'
                },
                {
                  icon: <Users className="w-8 h-8" />,
                  title: 'Professional Services',
                  desc: 'Get expert help with CV reviews, application assistance, interview preparation, and career consultation.',
                  color: 'from-purple-500 to-pink-600'
                }
              ].map((item, i) => (
                <StaggerItem key={i}>
                  <Card hover className="h-full">
                    <CardBody className="p-8">
                      <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-6 text-white`}>
                        {item.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                    </CardBody>
                  </Card>
                </StaggerItem>
              ))}
            </div>
          </Stagger>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Our Impact</h2>
          </FadeIn>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { value: '10,000+', label: 'Job Seekers', icon: <Users className="w-8 h-8" /> },
              { value: '500+', label: 'Verified Employers', icon: <Briefcase className="w-8 h-8" /> },
              { value: '5,000+', label: 'Successful Placements', icon: <TrendingUp className="w-8 h-8" /> }
            ].map((stat, i) => (
              <ScaleIn key={i} delay={i * 0.1}>
                <Card className="text-center">
                  <CardBody className="p-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-600">
                      {stat.icon}
                    </div>
                    <div className="text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                    <div className="text-gray-600 text-lg">{stat.label}</div>
                  </CardBody>
                </Card>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-16">Our Values</h2>
          </FadeIn>
          
          <Stagger>
            <div className="space-y-6">
              {[
                { icon: <Shield className="w-6 h-6" />, title: 'Trust & Safety', desc: 'All opportunities are verified to protect users from fraud and scams.' },
                { icon: <Globe className="w-6 h-6" />, title: 'Equal Access', desc: 'Providing opportunities to everyone regardless of location or background.' },
                { icon: <CheckCircle className="w-6 h-6" />, title: 'Quality Service', desc: 'Delivering professional support to help users succeed in their careers.' },
                { icon: <TrendingUp className="w-6 h-6" />, title: 'Innovation', desc: 'Using technology to solve Africa\'s employment and education challenges.' }
              ].map((value, i) => (
                <StaggerItem key={i}>
                  <div className="flex items-start gap-4 p-6 bg-white rounded-xl hover:shadow-md transition">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 flex-shrink-0">
                      {value.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                      <p className="text-gray-600">{value.desc}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </div>
          </Stagger>
        </div>
      </section>

      {/* FAQs Section - only shown when FAQs exist */}
      {faqs.length > 0 && (
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
                <span className="text-sm font-semibold">Got Questions?</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-500">Quick answers to the most common questions about BSAF</p>
            </div>
          </FadeIn>

          <Stagger>
            <div className="space-y-3">
              {faqs.slice(0, 6).map(faq => (
                <StaggerItem key={faq._id}>
                  <div className="border border-gray-200 rounded-xl overflow-hidden hover:border-blue-200 hover:shadow-sm transition">
                    <button
                      onClick={() => setOpenFaq(openFaq === faq._id ? null : faq._id)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-gray-50 transition"
                    >
                      <span className="font-semibold text-gray-900 pr-4 text-base">{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 text-blue-600 shrink-0 transition-transform duration-200 ${openFaq === faq._id ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq === faq._id && (
                      <div className="px-6 py-5 bg-blue-50 border-t border-blue-100 text-gray-700 leading-relaxed">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                </StaggerItem>
              ))}
            </div>
          </Stagger>

          {faqs.length > 6 && (
            <div className="text-center mt-8">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition"
              >
                View all {faqs.length} FAQs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="text-4xl font-bold mb-6">Join Us Today</h2>
            <p className="text-xl text-blue-100 mb-10">
              Be part of Africa's largest verified opportunities platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <button className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition shadow-lg">
                  Create Account
                  <ArrowRight className="w-5 h-5" />
                </button>
              </Link>
              <Link to="/jobs">
                <button className="flex items-center justify-center gap-2 px-8 py-4 bg-blue-500/20 backdrop-blur-sm text-white rounded-lg font-semibold border-2 border-white/30 hover:bg-blue-500/30 transition">
                  Browse Jobs
                  <Briefcase className="w-5 h-5" />
                </button>
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default About;
